import { UIElement } from '../ui-element.js';
import {
    createElement,
    dispatchNativeEvent,
    hide,
    insertElements,
    prerenderElementsTree,
    show,
} from '../../global/ui-helpers.js';
import { Labels } from '../../global/labels.js';
import { Component } from '../../decorators/component.decorator.js';
import { Attribute } from '../../decorators/attribute.decorator.js';
import { Child } from '../../decorators/child.decorator.js';
import { UIButton } from '../button/ui-button.js';
import type { TranslationLabels } from '../../types/labels.type.js';
import type { EventHandler, NullOr } from '../../global/utils.type.js';
import { Digest } from '../../global/index.js';
import { Deprecated } from '../../decorators/deprecated.decorator.js';
import styles from './ui-searchinput.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UISearchInput
 * @element ui-searchinput
 * @classdesc Represents a class for <code>ui-searchinput</code> element.
 * Input field used for search purposes.
 * @fires event:clear
 * @property {boolean} [cleanable] {@attr cleanable} If true, field value can
 * be cleaned with button.
 * @property {boolean} [searchable] {@attr searchable} If true, button with search
 * icon will be added.
 * @property {string} [controlId] {@attr control-id} Control (input element) id, if not set
 * will be generated automatically.
 * @property {string} uuid {@readonly} Unique id of the control.
 * @property {HTMLInputElement} control {@readonly} Shortcut to control.
 * @property {HTMLButtonElement} clearButton {@readonly} Shortcut to clean button if present.
 * @property {HTMLLabelElement} label {@readonly} Shortcut to clean label.
 * @slot
 * @example
 * <ui-searchinput cleanable>
 *   <input name="search" type="search" id="test-render-id">
 * </ui-searchinput>
 */
@Component({
    tag: 'ui-searchinput',
    styles,
})
export class UISearchInput extends UIElement {
    @Deprecated('searchable is always true, kept for backward compatibility')
    @Attribute(Boolean)
    searchable = true;
    @Attribute(Boolean) cleanable!: boolean;
    @Attribute(String) controlId!: string;
    @Child('input') control!: HTMLInputElement;
    @Child('button.ui-searchinput__clear') clearButton!: HTMLButtonElement;
    @Child('.ui-searchinput__label') label!: HTMLLabelElement;

    @Deprecated('use clearButton instead')
    @Child('button.ui-searchinput__clear')
    clearBtn!: HTMLButtonElement;

    private inputFocusHandler!: EventHandler;
    private inputBlurHandler!: EventHandler;
    private inputUpdateHandler!: EventHandler;
    private clearHandler!: EventHandler;
    private labelClickHandler!: EventHandler;

    static get observedAttributes(): Array<string> {
        return ['control-id'];
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-searchinput', {
            clear: 'Clear input',
        });
    }

    get uuid(): string {
        if (!this.control.id) {
            this.control.id = Digest.randomId();
        }
        return this.control.id;
    }

    /**
     * Handles clean event.
     * @param {Event} [e]
     * @private
     */
    handleClear(e?: Event) {
        const clearEvent = this.dispatchCustomEvent('clear', {}, true);
        if (clearEvent.defaultPrevented) {
            return;
        }
        hide(this.clearButton);
        this.control.value = '';
        dispatchNativeEvent(this.control, 'change', true);
        this.control.focus();
    }

    /**
     * Updates appearance regarding input value.
     */
    updateStatus(): void {
        if (this.cleanable) {
            this.control.value
                ? show(this.clearButton)
                : hide(this.clearButton);
        } else {
            hide(this.clearButton);
        }
    }

    /**
     * Handles update on input.
     * @param {Event} [event]
     * @private
     */
    handleInputUpdates(event?: Event): void {
        this.updateStatus();
    }

    /**
     * Handles focus on input.
     * @param {Event} [event]
     * @private
     */
    handleInputFocus(event?: Event): void {
        this.classList.add('-focus');
    }

    /**
     * Handles blur on input.
     * @param {Event} [event]
     * @private
     */
    handleInputBlur(event?: Event): void {
        this.classList.remove('-focus');
    }

    /**
     * @inheritDoc
     */
    observeAttributes(name: string, oldValue: string, newValue: string): void {
        switch (name) {
            case 'control-id':
                if (this.control) {
                    if (newValue) {
                        this.control.setAttribute('id', newValue);
                    } else {
                        this.control.removeAttribute('id');
                    }
                }
                break;
        }
    }

    private renderClearButton(): NullOr<UIButton> {
        return createElement<UIButton>({
            tagName: 'ui-button',
            attributes: {
                'layout': UIButton.Layout.Tertiary,
                'aria-label': UISearchInput.labels.clear,
                'glyph': 'redesign-circle-x-mark',
                'type': 'button',
            },
            classList: {
                'ui-searchinput__clear': true,
            },
        });
    }

    private renderSearchLabel(): NullOr<HTMLLabelElement> {
        return createElement<HTMLLabelElement>({
            tagName: 'label',
            attributes: {
                for: this.uuid,
            },
            classList: {
                'ui-searchinput__label': true,
            },
            children: [
                {
                    tagName: 'ui-icon',
                    attributes: {
                        glyph: 'redesign-magnifying-glass',
                    },
                },
            ],
        });
    }

    private subscribeEvents(): void {
        this.control.addEventListener('focus', this.inputFocusHandler);
        this.control.addEventListener('blur', this.inputBlurHandler);
        if (this.cleanable) {
            this.control.addEventListener('input', this.inputUpdateHandler);
            this.clearButton.addEventListener('click', this.clearHandler);
        }
        this.label.addEventListener('click', this.labelClickHandler);
    }

    private unsubscribeEvents(): void {
        this.control.removeEventListener('focus', this.inputFocusHandler);
        this.control.removeEventListener('blur', this.inputBlurHandler);
        if (this.cleanable) {
            this.control.removeEventListener('input', this.inputUpdateHandler);
            this.clearButton.removeEventListener('click', this.clearHandler);
        }
        this.label.removeEventListener('click', this.labelClickHandler);
    }

    /**
     * @inheritDoc
     */
    render(): void {
        const input = this.querySelector<HTMLInputElement>('input');
        if (!input) {
            return;
        }
        const clearButton = this.renderClearButton();
        const searchLabel = this.renderSearchLabel();

        // TODO GUI-3394 remove prerender when button issue is resolved.
        prerenderElementsTree(clearButton as UIElement);

        insertElements(this, [
            {
                tagName: 'div',
                classList: {
                    'ui-searchinput__icon': true,
                },
                children: [clearButton, searchLabel],
            },
        ]);
        this.updateStatus();
        if (input.type !== 'search') {
            input.setAttribute('role', 'searchbox');
        }
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.inputFocusHandler = this.handleInputFocus.bind(this);
        this.inputBlurHandler = this.handleInputBlur.bind(this);
        this.inputUpdateHandler = this.handleInputUpdates.bind(this);
        this.clearHandler = this.handleClear.bind(this);
        this.labelClickHandler = () => this.control.focus();
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.unsubscribeEvents();
    }
}
